import { useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Search, FileText, Image as ImageIcon } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RESEARCH_SEARCH } from '../../graphql';
import { ResearchSearchResponse, ResearchHit } from '../../types/graphql';
import { SourceCard } from './SourceCard';

interface ResearchExplorerProps {
  accountId: string;
  lang: string;
}

interface ArticleGroup {
  key: string;
  articleTitle: string;
  hits: ResearchHit[];
}

function groupByArticle(hits: ResearchHit[]): ArticleGroup[] {
  const groups = new Map<string, ArticleGroup>();
  for (const hit of hits) {
    const key = `${hit.topicId || hit.postId || 'unknown'}::${hit.articleTitle ?? ''}`;
    let group = groups.get(key);
    if (!group) {
      group = { key, articleTitle: hit.articleTitle || 'Untitled article', hits: [] };
      groups.set(key, group);
    }
    group.hits.push(hit);
  }
  return Array.from(groups.values());
}

export function ResearchExplorer({ accountId, lang }: ResearchExplorerProps) {
  const client = useApolloClient();
  const [input, setInput] = useState('');
  const [hits, setHits] = useState<ResearchHit[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [groupArticles, setGroupArticles] = useState(true);

  const search = async () => {
    const query = input.trim();
    if (!query || loading) return;
    setLoading(true);
    setError('');

    try {
      const { data } = await client.query<ResearchSearchResponse>({
        query: RESEARCH_SEARCH,
        variables: { accountId, query, lang },
        fetchPolicy: 'network-only',
      });
      setHits(data.researchSearch);
    } catch (err: any) {
      setError(err.message || 'Search failed.');
      setHits(null);
    } finally {
      setLoading(false);
    }
  };

  const groups = useMemo(() => (hits ? groupByArticle(hits) : []), [hits]);
  const isPhotoResults = hits !== null && hits.length > 0 && hits[0].type === 'photo';
  const isRtl = lang === 'ar';

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-shrink-0 space-y-3 pb-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            search();
          }}
          className="flex gap-2"
        >
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              isRtl ? 'ابحث في المحتوى المفهرس...' : 'Search indexed articles and facts...'
            }
            dir={isRtl ? 'rtl' : 'ltr'}
            className="flex-1 px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <Button
            type="submit"
            disabled={loading || !input.trim()}
            className="bg-button-primary text-button-primary gap-2"
          >
            <Search className="w-4 h-4" />
            Search
          </Button>
        </form>
        <div className="flex items-center gap-4 text-sm text-cmp-ink-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={groupArticles}
              onChange={(e) => setGroupArticles(e.target.checked)}
              className="accent-[#2babaf]"
            />
            Group by article
          </label>
          <span className="text-xs">
            Tip: include words like "photo" or "image" to search the photo index.
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 space-y-4 pb-4">
        {error && (
          <div className="bg-form-dark border border-error rounded-lg p-4 text-error text-sm">
            {error}
          </div>
        )}

        {loading && (
          <div className="flex items-center gap-3 text-cmp-ink-2 py-8 justify-center">
            <div className="w-4 h-4 border-2 border-cmp-line-2 border-t-primary rounded-full animate-spin" />
            Searching the index...
          </div>
        )}

        {!loading && hits === null && !error && (
          <div className="h-full flex flex-col items-center justify-center text-center text-cmp-ink-2 gap-3 py-12">
            <Search className="w-10 h-10 text-primary" />
            <p className="max-w-md">
              Browse the raw indexed content — extracted facts grouped by source article, or archive
              photos. This is retrieval only; no AI synthesis.
            </p>
          </div>
        )}

        {!loading && hits !== null && hits.length === 0 && (
          <div className="text-center text-cmp-ink-2 py-12">
            No indexed content matched that search.
          </div>
        )}

        {!loading && hits !== null && hits.length > 0 && (
          <>
            <div className="flex items-center gap-2 text-sm text-cmp-ink-2">
              {isPhotoResults ? (
                <ImageIcon className="w-4 h-4" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
              {hits.length} {isPhotoResults ? 'photos' : 'facts'}
              {groupArticles && !isPhotoResults && ` across ${groups.length} articles`}
            </div>

            {groupArticles ? (
              groups.map((group) => (
                <div key={group.key} className="bg-core-dark rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h4 className="text-cmp-ink text-sm font-semibold">{group.articleTitle}</h4>
                    <Badge className="bg-form-dark text-cmp-ink border border-cmp-line-2 flex-shrink-0">
                      {group.hits.length} {group.hits.length === 1 ? 'item' : 'items'}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {group.hits.map((hit, i) => (
                      <SourceCard key={i} hit={hit} lang={lang} />
                    ))}
                  </div>
                  {(() => {
                    const meta = new Set<string>();
                    for (const hit of group.hits) {
                      for (const t of hit.topics || []) meta.add(t);
                      for (const p of hit.keyPeople || []) meta.add(p);
                    }
                    const items = Array.from(meta).slice(0, 8);
                    if (items.length === 0) return null;
                    return (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {items.map((item) => (
                          <span
                            key={item}
                            className="text-xs text-cmp-ink-2 bg-form-dark px-2 py-0.5 rounded-full border border-cmp-line"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              ))
            ) : (
              <div className="space-y-2">
                {hits.map((hit, i) => (
                  <SourceCard key={i} hit={hit} lang={lang} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
